/**
 * Images are prepared in the browser before upload, like SMYK's Sveltia setup: raster images scaled to fit
 * max_width × max_height and converted to WebP (quality from the config), SVGs cleaned up. GIFs are kept as they
 * are (they may be animated). Browsers that can't encode WebP (older Safari) get JPEG instead.
 */
import { safeFileName, type UploadConfig } from '@hotpot/core';

export interface PreparedImage {
  name: string;
  base64: string;
  bytes: number;
  previewUrl: string;
}

const DEFAULTS: Required<Pick<UploadConfig, 'max_width' | 'max_height' | 'quality' | 'max_file_size'>> = {
  max_width: 2400,
  max_height: 2400,
  quality: 85,
  max_file_size: 50 * 1024 * 1024,
};

export class ImageError extends Error {
  constructor(
    public code: 'too_big' | 'not_image',
    public fileName: string,
    public limitMb = 0,
  ) {
    super(code);
  }
}

export async function prepareImage(file: File, config: UploadConfig = {}, keepAs?: string): Promise<PreparedImage> {
  const max = config.max_file_size ?? DEFAULTS.max_file_size;
  if (file.size > max) throw new ImageError('too_big', file.name, Math.round(max / 1024 / 1024));
  const type = file.type || guessType(file.name);
  if (!type.startsWith('image/')) throw new ImageError('not_image', file.name);

  // Replacing a file keeps its name, so the new image is encoded in the old file's format.
  const target = keepAs ? targetType(keepAs) : null;
  if (type === 'image/svg+xml' && (!target || target === 'image/svg+xml')) {
    const text = cleanSvg(await file.text(), config.svg_optimize !== false);
    const bytes = new TextEncoder().encode(text);
    return { name: safeFileName(file.name), base64: toBase64(bytes), bytes: bytes.length, previewUrl: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(text)}` };
  }
  if ((type === 'image/gif' && (!target || target === 'image/gif')) || (!target && config.format === 'keep')) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    return { name: safeFileName(file.name), base64: toBase64(bytes), bytes: bytes.length, previewUrl: URL.createObjectURL(file) };
  }

  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions).catch(() => {
    throw new ImageError('not_image', file.name);
  });
  const maxW = config.max_width ?? DEFAULTS.max_width;
  const maxH = config.max_height ?? DEFAULTS.max_height;
  const scale = Math.min(1, maxW / bitmap.width, maxH / bitmap.height);
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  const quality = (config.quality ?? DEFAULTS.quality) / 100;
  if (target && target !== 'image/webp') {
    const out = await new Promise<Blob | null>((r) => canvas.toBlob(r, target === 'image/png' ? 'image/png' : 'image/jpeg', quality));
    if (!out) throw new ImageError('not_image', file.name);
    const bytes = new Uint8Array(await out.arrayBuffer());
    return { name: keepAs!, base64: toBase64(bytes), bytes: bytes.length, previewUrl: URL.createObjectURL(out) };
  }
  let blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/webp', quality));
  let ext = 'webp';
  if (!blob || blob.type !== 'image/webp') {
    blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', quality));
    ext = 'jpg';
  }
  if (!blob) throw new ImageError('not_image', file.name);
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const stem = safeFileName(file.name).replace(/\.[^.]+$/, '');
  return { name: `${stem}.${ext}`, base64: toBase64(bytes), bytes: bytes.length, previewUrl: URL.createObjectURL(blob) };
}

/** Light SVG optimisation, and removal of anything that could run code (scripts, event handlers, links). */
export function cleanSvg(svg: string, optimize: boolean): string {
  let s = svg
    .replace(/<script[\s\S]*?<\/script\s*>/gi, '')
    .replace(/<foreignObject[\s\S]*?<\/foreignObject\s*>/gi, '')
    .replace(/\s+on[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href\s*=\s*["'])\s*javascript:[^"']*/gi, '$1#');
  if (optimize) {
    s = s
      .replace(/<\?xml[\s\S]*?\?>/g, '')
      .replace(/<!DOCTYPE[\s\S]*?>/gi, '')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/<metadata[\s\S]*?<\/metadata>/gi, '')
      .replace(/\s+(sketch|inkscape|sodipodi):[\w-]+="[^"]*"/g, '')
      .replace(/>\s+</g, '><')
      .trim();
  }
  return s;
}

function targetType(name: string): string {
  const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
  return ({ jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml' } as Record<string, string>)[ext] ?? 'image/webp';
}

function guessType(name: string): string {
  const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
  return ({ jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', heic: 'image/heic', avif: 'image/avif' } as Record<string, string>)[ext] ?? '';
}

export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/** A file name that doesn't clash with `taken` (adds -2, -3 before the extension). */
export function uniqueName(name: string, taken: Set<string>): string {
  if (!taken.has(name)) return name;
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  for (let i = 2; ; i++) if (!taken.has(`${stem}-${i}${ext}`)) return `${stem}-${i}${ext}`;
}
